import {
  DisplayIf,
  DisplayIf_Operator,
  PrimitiveValue,
  RoundingMode,
  InputData,
} from '@policyfly/protobuf/pfschema'

import { COUNTRY_CODE_STRING_MAP, DECIMAL_RANGE_SEPARATOR } from './constants'
import { big } from './number'

import type {
  Agency,
  CountryCode,
  CoverHolder,
  Date as DateGrpc,
  Decimal,
  Money,
} from '@policyfly/protobuf'
import type { Timestamp } from '@policyfly/protobuf/google/protobuf'
import type { UserDetail } from '@policyfly/protobuf/patrick'
import type { AnyObject, PrimitiveValue as PrimitiveValueSchema } from '@policyfly/types/common'
import type { RoundingMode as BigRoundingMode } from 'big.js'

/**
 * Used to multiply/divide the `part` of a {@link Decimal} or the `nanos` of a {@link Money}.
 * This is so we can maintain precision of numbers such as '1.0004'.
*/
export const DECIMAL_PRECISION = 1_000_000_000

/**
 * Converts a string date to a {@link DateGrpc Date} protobuf struct.
 * Returns `null` if conversion is not possible.
 */
export function stringToDate (date: string | null): DateGrpc | null {
  if (!date || typeof date !== 'string') return null
  const [year, month, day] = date.split('-')
  return { year: +year, month: +month, day: +day }
}

/**
 * Converts a {@link DateGrpc Date} protobuf struct to a string date.
 * Returns `null` if conversion is not possible.
 */
export function dateToString (date: DateGrpc | null | undefined): string | null {
  if (!date || typeof date !== 'object') return null
  const { year, month, day } = date
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/**
 * Converts a string date to a {@link Timestamp} protobuf struct.
 * Returns `null` if conversion is not possible.
 */
export function stringToTimestamp (date: string | null): Timestamp | null {
  if (!date || typeof date !== 'string') return null
  return {
    nanos: 0,
    seconds: String(new Date(date).valueOf() / 1000),
  }
}

/**
 * Converts a {@link Timestamp} protobuf struct to a string date.
 * Returns `null` if conversion is not possible.
 *
 * @param timestamp
 * @param full If `true` will return the full ISO date, otherwise returns just the `yyyy-mm-dd`. Defaults to `false`.
 */
export function timestampToString (timestamp: Timestamp | null | undefined, full = false): string | null {
  if (!timestamp || typeof timestamp !== 'object') return null
  const { seconds } = timestamp
  const isoString = new Date(Number(seconds) * 1000).toISOString()
  return full ? isoString : isoString.split('T')[0]
}

export function timestampToMilliseconds (timestamp: Timestamp | null | undefined): number {
  if (!timestamp || typeof timestamp !== 'object') return 0
  return (+(timestamp.seconds || 0)) * 1000 + (timestamp?.nanos ?? 0) / 1_000_000
}

/**
 * Converts a string (or number) decimal to a {@link Decimal} protobuf struct.
 * Returns `null` if conversion is not possible.
 */
export function stringToDecimal (amount: string | number | null): Decimal | null {
  if (!amount && amount !== 0) return null
  const [rawWhole, rawPart] = String(amount).split('.')
  const isNegative = rawWhole.startsWith('-')
  const whole = rawWhole === '-0' ? '0' : rawWhole
  const part = big(`0.${rawPart || 0}`)
    .mul(DECIMAL_PRECISION)
    // part needs to be negative too unless it is `0`
    .mul(isNegative ? -1 : 1)
    .toString()
  return { whole, part }
}

/**
 * Converts a {@link Decimal} protobuf struct to a string decimal.
 * If a range is specified, will separate the low and high values with a {@link DECIMAL_RANGE_SEPARATOR}.
 *
 * Returns `null` if conversion is not possible.
 */
export function decimalToString (decimal: Decimal | null | undefined): string {
  if (!decimal) return '0.00'
  const { whole, part, range } = decimal

  // if a range exists it must be an unsettled amount
  if (range) {
    const low = decimalToString(range.low)
    const high = decimalToString(range.high)
    return [low, high].join(DECIMAL_RANGE_SEPARATOR)
  }

  // negative parts are sent with a leading `-` too
  const isNegative = whole.startsWith('-') || part.startsWith('-')
  const bigPart = big(part).abs()
  if (bigPart.eq(0)) {
    return whole
  } else {
    const returnWhole = big(whole).abs().toString()
    const returnPart = bigPart.div(DECIMAL_PRECISION).toString().split('.')[1]
    const returnValue = `${returnWhole}.${returnPart}`
    return isNegative ? `-${returnValue}` : returnValue
  }
}

/**
 * Converts from a {@link RoundingMode protobuf RoundingMode} to a {@link BigRoundingMode Big.js RoundingMode}.
 */
export function roundingModeToBigRoundingMode (roundingMode: RoundingMode): BigRoundingMode {
  switch (roundingMode) {
    case RoundingMode.ROUNDING_MODE_HALF_UP: return 1
    case RoundingMode.ROUNDING_MODE_HALF_EVEN: return 2
    case RoundingMode.ROUNDING_MODE_UP: return 3
    default: return 0
  }
}

/**
 * Converts a {@link CountryCode} to a string country name.
 */
export function countryCodeToString (code: CountryCode): string {
  return COUNTRY_CODE_STRING_MAP.get(code) || ''
}

/**
 * Converts an {@link Agency} or a {@link CoverHolder} to a string that stores ids.
 */
export function agencyOrCoverHolderToString (message: Agency | CoverHolder | null): string | null {
  return message?.ulid || null
}

/**
 * Converts a string that stores ids to an {@link Agency} or a {@link CoverHolder}.
 */
export function stringToAgencyOrCoverHolder<T extends Agency | CoverHolder> (id: string | null): T | null {
  if (!id) return null
  return { id: 0, ulid: id } as T
}

/**
 * Converts a {@link UserDetail} to a tuple string that stores ids.
 */
export function userDetailToString (message: UserDetail | null | undefined): string | null {
  if (!message?.legacyId && !message?.bingoId) return null
  return `${message.legacyId || 'null'}|${message.bingoId || 'null'}`
}

/**
 * Converts a tuple string that stores ids to a {@link UserDetail}.
 */
export function stringToUserDetail (ids: string | null): UserDetail | null {
  if (typeof ids !== 'string') return null
  const [legacyId, bingoId] = ids.split('|')
  if (!legacyId || !bingoId) return null
  const message = { legacyId: 0, bingoId: '' } as UserDetail
  const numericId = +legacyId
  if (!isNaN(numericId)) message.legacyId = numericId
  if (bingoId && bingoId !== 'null') message.bingoId = bingoId
  return message
}

/**
 * Converts a {@link Money} protobuf struct to a string decimal.
 *
 * Returns `null` if value is empty or conversion is not possible.
 */
export function moneyToString (money: Money | null | undefined): string | null {
  if (!money || typeof money !== 'object') return null
  const { units, nanos } = money
  const isNegative = units.startsWith('-') || nanos < 0
  const bigNanos = big(nanos).abs()
  if (bigNanos.eq(0)) {
    return units
  } else {
    const returnWhole = big(units).abs().toString()
    const returnPart = bigNanos.abs().div(DECIMAL_PRECISION).toString().split('.')[1]
    const returnValue = `${returnWhole}.${returnPart}`
    return isNegative ? `-${returnValue}` : returnValue
  }
}

/**
 * Converts a string (or number) decimal to a {@link Money} protobuf struct.
 * Returns `null` if conversion is not possible.
 */
export function stringToMoney (amount: string | number | null): Money | null {
  if (!amount && amount !== 0) return null
  const [rawWhole, rawPart] = String(amount).split('.')
  const isNegative = rawWhole.startsWith('-')
  const units = rawWhole === '-0' ? '0' : rawWhole
  const nanos = big(`0.${rawPart || 0}`)
    .mul(DECIMAL_PRECISION)
    // part needs to be negative too unless it is `0`
    .mul(isNegative ? -1 : 1)
    .toNumber()
  return { currencyCode: '', units, nanos }
}

/**
 * Converts a primitive value to a {@link PrimitiveValue} protobuf struct.
 */
export function convertPrimitiveValue (v?: boolean | null | undefined | string | number): PrimitiveValue
export function convertPrimitiveValue (v?: unknown): undefined
export function convertPrimitiveValue (v?: unknown): PrimitiveValue | undefined {
  switch (typeof v) {
    case 'string': return PrimitiveValue.create({ value: { oneofKind: 'string', string: v } })
    case 'number': return PrimitiveValue.create({ value: { oneofKind: 'number', number: v } })
    case 'boolean': return PrimitiveValue.create({ value: { oneofKind: 'bool', bool: v } })
  }
  if (v === null) return PrimitiveValue.create({ value: { oneofKind: 'null', null: true } })
  if (v === undefined) return PrimitiveValue.create({ value: { oneofKind: 'undefined', undefined: true } })
}

/**
 * Reverts a protobuf {@link PrimitiveValue PrimitiveValue} to a JS {@link PrimitiveValueSchema primitive} or `undefined`.
 */
export function revertPrimitiveValue (message?: PrimitiveValue): PrimitiveValueSchema | undefined {
  if (!message?.value.oneofKind) return undefined
  switch (message.value.oneofKind) {
    case 'bool': return message.value.bool
    case 'number': return message.value.number
    case 'string': return message.value.string
    case 'null': return null
    default:
      return undefined
  }
}

/**
 * Merges multiple {@link DisplayIf} to a single one where every condition must be met.
 * You can also pass `undefined` conditions which are skipped when trying to merge.
 * Due to the fact all conditions must be true ordering does not matter.
 *
 * @returns
 * - `undefined` if no {@link DisplayIf} are passed
 * - the single {@link DisplayIf} if only one {@link DisplayIf} is passed
 * - a new {@link DisplayIf} which is an AND of all {@link DisplayIf} conditions if more than one is passed
 */
export function mergeDisplayIf (displayIfConditions: (DisplayIf | undefined)[]): DisplayIf | undefined {
  const conditions: DisplayIf[] = displayIfConditions.filter((d): d is DisplayIf => !!d)
  if (conditions.length === 0) return undefined
  if (conditions.length === 1) return conditions[0]
  return DisplayIf.create({
    operator: DisplayIf_Operator.OPERATOR_AND,
    conditions: conditions
      .map((d) => ({ type: { oneofKind: 'displayIf', displayIf: d } })),
  })
}

/**
 * Clones the provided {@link InputData} with path set to a new string.
 * This will set correctly depending on the provided source.
 *
 * @example
 * const inputData1 = InputData.create({ source: { oneofKind: 'pb', pb: { path: 'path1' } } })
 * const inputData2 = cloneInputDataWithPath(inputData1, 'path2')
 * console.log(inputData2.source.pb.path) // 'path2'
 */
export function cloneInputDataWithPath (inputData: InputData, path: string): InputData {
  const inputDataClone = InputData.clone(inputData)
  // clear existing sources to avoid multiple being set
  for (const key in inputDataClone.source) {
    if (key !== 'oneofKind' && inputDataClone.source.oneofKind !== key) {
      delete (inputDataClone.source as AnyObject)[key]
    }
  }
  switch (inputDataClone.source.oneofKind) {
    case 'binder': inputDataClone.source.binder.path = { value: { oneofKind: 'string', string: path } }; break
    case 'computed': inputDataClone.source.computed.path = { value: { oneofKind: 'string', string: path } }; break
    case 'external': inputDataClone.source.external.path = { value: { oneofKind: 'string', string: path } }; break
    case 'flag': inputDataClone.source.flag.path = path; break
    case 'issue': inputDataClone.source.issue.path = { value: { oneofKind: 'string', string: path } }; break
    case 'license': inputDataClone.source.license.path = { value: { oneofKind: 'string', string: path } }; break
    case 'local': inputDataClone.source.local.path = { value: { oneofKind: 'string', string: path } }; break
    case 'meta': inputDataClone.source.meta.path = { value: { oneofKind: 'string', string: path } }; break
    case 'parent': inputDataClone.source.parent.path = { value: { oneofKind: 'string', string: path } }; break
    case 'path': inputDataClone.source.path.path = { value: { oneofKind: 'string', string: path } }; break
    case 'pb': inputDataClone.source.pb.path = path; break
    case 'programData': inputDataClone.source.programData.path = { value: { oneofKind: 'string', string: path } }; break
    case 'quote': inputDataClone.source.quote.path = { value: { oneofKind: 'string', string: path } }; break
    case 'rating': inputDataClone.source.rating.path = { value: { oneofKind: 'string', string: path } }; break
    case 'recent': inputDataClone.source.recent.path = { value: { oneofKind: 'string', string: path } }; break
    case 'responses': inputDataClone.source.responses.path = { value: { oneofKind: 'string', string: path } }; break
    case 'store': inputDataClone.source.store.path = path; break
    case 'temp': inputDataClone.source.temp.path = { value: { oneofKind: 'string', string: path } }; break
    default: {
      const invalid: undefined | never = inputDataClone.source.oneofKind
      console.warn(`Unsupported InputData source type: ${invalid}`)
    }
  }
  return inputDataClone
}

type OneOfValue<T extends { oneofKind: PropertyKey | undefined }> =
  T extends { oneofKind: infer K }
    ? K extends keyof T
      ? T[K]
      : undefined
    : undefined
/**
 * Gets the selected value from a `oneofKind` union.
 * The type returned is a union of all possible values in the union.
 *
 * This is an alternative to `getSelectedOneofValue` which doesn't always seem
 * to infer types in the way we expect.
 */
export function getOneofKindValue<T extends { oneofKind: PropertyKey | undefined }> (
  value: T,
): OneOfValue<T> {
  if (!value.oneofKind) return undefined as OneOfValue<T>
  return value[value.oneofKind as keyof T] as OneOfValue<T>
}
